import { BorrowerNaturalPersonDto } from 'api/borrower';
import { QuestionAnswerDto } from 'api/quote';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ActorType } from 'features/new-actor/enums/actorType';
import { mapActorTypeBorrower } from 'features/new-actor/mappers/optim/mapActorTypeBorrower';
import { mapBorrowerNaturalPersonFormValues } from 'features/new-actor/mappers/optim/mapBorrowerNaturalPersonFormValues';
import { selectReferentialData } from 'features/referentials/selectors/selectReferentialDataByKey';
import { selectPendingForm } from 'features/subscription-process/slices/selectors';
import { useBorrowerProjectId } from 'features/subscription-process/hooks/useBorrowerProjectId';
import { resetPendingForm } from 'features/subscription-process/slices/subscriptionProcessSlice';
import { FormikProps } from 'formik';
import { usePanel } from 'hooks/usePanel';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { borrowersAPI } from 'services/swagger-api';
import { ProfilingMode } from '../../slices/types/enums';
import {
  ContextCode,
  SurveyName,
  useGenericSurvey
} from '../../hooks/useGenericSurvey';
import { BorrowerInsuredFormValues } from './borrowerInsuredForm';

function mapSurveyAnswers(
  questionId: string,
  value: any
): QuestionAnswerDto[] {
  if (Array.isArray(value)) {
    return value.map((item) => mapSingleAnswer(questionId, item));
  }

  return [mapSingleAnswer(questionId, value)];
}

function mapSingleAnswer(questionId: string, value: any): QuestionAnswerDto {
  if (value && typeof value === 'object' && value.key) {
    return {
      questionId,
      possibleAnswerId: value.id
    };
  }

  if (typeof value === 'boolean') {
    return {
      questionId,
      answerText: value.toString()
    };
  }

  return {
    questionId,
    answerText: value?.toString() || ''
  };
}

export const useBorrowerInsuredForm = (
  reload: () => void,
  personId?: string,
  borrowerProjectIdProp?: string
) => {
  // Id du projet : prop de l'ouvrant en priorité (fiable en panneau), sinon l'URL.
  const borrowerProjectIdFromUrl = useBorrowerProjectId();
  const borrowerProjectId = borrowerProjectIdProp ?? borrowerProjectIdFromUrl;
  const { getSurvey, transformFormValues, mapToSurveyFormValue } =
    useGenericSurvey();
  const dispatch = useAppDispatch();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoadingPerson] = useState(false);
  const { entity: selectedPerson } = useAppSelector(selectPendingForm());
  const referentialData = useAppSelector(selectReferentialData);

  const { setPanelActions, closePanel } = usePanel();
  const formikRef = useRef<FormikProps<BorrowerInsuredFormValues>>(null);
  const { initSurvey } = useGenericSurvey();

  const [pendingBorrower, setPendingBorrower] =
    useState<BorrowerNaturalPersonDto>();
  const [currentPersonId, setCurrentPersonId] = useState<string>();
  const [answers, setAnswers] = useState<Array<QuestionAnswerDto>>([]);

  useEffect(() => {
    if (personId) {
      setCurrentPersonId(personId);
    }
  }, [personId]);

  useEffect(() => {
    if (selectedPerson) {
      setCurrentPersonId(selectedPerson.id);
    }
  }, [selectedPerson]);

  useEffect(() => {
    initSurvey(
      ContextCode.BorrowerPerson,
      SurveyName.BorrowerPerson,
      ProfilingMode.Group
    );
  }, [initSurvey, dispatch]);

  const deleteInsuredBorrower = useCallback(async () => {
    if (!borrowerProjectId || !pendingBorrower?.borrowerId) return;

    try {
      await borrowersAPI.deleteBorrower({
        borrowerProjectId,
        borrowerId: pendingBorrower.borrowerId
      });
      reload();
    } catch (error: any) {
      console.error('Error deleting borrower:', error);
      toast.error(
        error?.response?.error?.message || 'Erreur lors de la suppression'
      );
    }
  }, [borrowerProjectId, pendingBorrower?.borrowerId, reload]);

  useEffect(() => {
    const getBorrowerPerson = async () => {
      if (!currentPersonId || !borrowerProjectId) return;

      setIsLoadingPerson(true);

      try {
        const response = await borrowersAPI.getBorrowerProjectPerson({
          borrowerProjectId,
          personId: currentPersonId,
          includeQuestions: true
        });

        if (response.naturalPerson) {
          setPendingBorrower(response.naturalPerson);
          if (response?.saveAnswersCommand)
            setAnswers(response.saveAnswersCommand.answers);
        }
      } catch (error: any) {
        console.error('Error fetching real estate:', error);
        toast.error(
          error?.response?.error?.message || 'Error loading borrower person data'
        );
      } finally {
        setIsLoadingPerson(false);
      }
    };

    getBorrowerPerson();
  }, [currentPersonId, dispatch, borrowerProjectId]);

  useEffect(() => {
    const basePanelActions = {
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      submitDisabled: !isFormValid || isLoading,
      onSubmit: () => {
        formikRef.current?.submitForm();
      },
      onClose: () => {
        dispatch(resetPendingForm());
      }
    };

    if (pendingBorrower) {
      setPanelActions({
        ...basePanelActions,
        onDelete: () => {
          deleteInsuredBorrower();
          dispatch(resetPendingForm());
          closePanel();
        },
        deleteLabel: 'COMMON.DELETE'
      });
      return;
    }

    if (formikRef.current) {
      setPanelActions(basePanelActions);
    }
  }, [
    dispatch,
    pendingBorrower,
    formikRef,
    isFormValid,
    isLoading,
    setPanelActions,
    deleteInsuredBorrower,
    closePanel
  ]);

  const upsertInsuredBorrower = useCallback(
    async (values: BorrowerInsuredFormValues) => {
      if (borrowerProjectId) {
        try {
          const mappedValues = mapBorrowerNaturalPersonFormValues(
            values,
            referentialData
          );
          const survey = await getSurvey(
            ContextCode.BorrowerPerson,
            SurveyName.BorrowerPerson
          );
          const transformedValues = transformFormValues(values);
          let answers: Array<QuestionAnswerDto> = [];
          if (transformedValues.surveyForm) {
            answers = Object.entries(transformedValues.surveyForm).flatMap(
              ([questionId, answersArray]) =>
                mapSurveyAnswers(questionId, answersArray)
            );
          }
          await borrowersAPI.upsertBorrowerPerson({
            borrowerProjectId,
            upsertBorrowerPersonCommandDto: {
              naturalPerson: { ...mappedValues },
              saveAnswersCommand: {
                answers: answers,
                surveyId: survey.id,
                autoEvaluate: true
              }
            }
          });
          reload();
          closePanel();
          dispatch(resetPendingForm());
        } catch (error) {
          console.error(error);
        }
      }
    },
    [
      referentialData,
      borrowerProjectId,
      transformFormValues,
      getSurvey,
      closePanel,
      dispatch,
      reload
    ]
  );

  const editConfig = useMemo(() => {
    if (!pendingBorrower) return;

    const isNaturalPerson =
      'lastName' in pendingBorrower && 'firstName' in pendingBorrower;
    const borrowerActorType = isNaturalPerson
      ? (ActorType.BorrowerNatural as const)
      : (ActorType.BorrowerNatural as const);
    return {
      initialValues: {
        ...mapActorTypeBorrower(
          borrowerActorType,
          pendingBorrower as BorrowerNaturalPersonDto,
          referentialData,
          mapToSurveyFormValue(answers)
        )
      },
      initialData: pendingBorrower,
      onSubmit: async (
        values: Partial<BorrowerInsuredFormValues>,
        _initialData: any,
        _actorType: ActorType
      ) => {
        if (values.id) {
          upsertInsuredBorrower(values as BorrowerInsuredFormValues);
        }
      }
    };
  }, [
    pendingBorrower,
    referentialData,
    upsertInsuredBorrower,
    answers,
    mapToSurveyFormValue
  ]);

  const handleSubmit = useCallback(
    async (values: BorrowerInsuredFormValues, _actorType: ActorType) => {
      upsertInsuredBorrower(values);
    },
    [upsertInsuredBorrower]
  );

  const handleValidationChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  return {
    editConfig,
    formikRef,
    handleSubmit,
    pendingBorrower,
    handleValidationChange,
    isLoading
  };
};
